import * as Sentry from "@sentry/angular";
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { UserField, UserModel } from 'src/app/shared/models/user/users';
import { TenantService } from 'src/app/shared/api-services/tenant.service';
import { UsersService } from 'src/app/shared/api-services/users.service';
import { Observable, Subject, concatMap, finalize, forkJoin, map, take, takeUntil, tap } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DropdownOption } from "../../dropdown/dropdown-primary/dropdown.interface";
import { HeadquartersService } from "src/app/shared/api-services/headquarters.service";
import { SafeUrl } from "@angular/platform-browser";
import { MeService } from "src/app/shared/api-services/me.service";
import { CommunicationService } from "src/app/shared/utilities/comunication.service";
import { InfoService } from "src/app/shared/api-services/info.service";
import { ExtraFieldsUtilsService } from "src/app/shared/utilities/extra-fields-utils.servic";
import { StorageUtilsService } from "src/app/shared/utilities/storage-utils.servic";
import { TypeExtraField } from "src/app/shared/models/common";
import { MeRequest } from "src/app/shared/models/me/me-request";
import { Me } from "src/app/shared/models/me/me";
import { UserRequest } from "src/app/shared/models/user/user-request";
import { CultureInfo, TenantInfo } from "src/app/shared/models/info/info";
import { DropdownModule } from "../../dropdown/dropdown.module";
import { CommonModule } from "@angular/common";
import { TooltipModule } from "primeng/tooltip";
import { InputDatePickerComponent } from "../../inputs/input-date-picker/input-date-picker.component";
import { SelectModule } from "../../inputs/select-primeng/select.module";
import { Headquarter } from "src/app/shared/models/headquarter/headquarter";
import { TimezoneOption } from "../../inputs/select-primeng/timezone-select/timezone-select.component";
import { LicenseType } from "../../inputs/select-primeng/license-type-select/license-type-select.component";

@Component({
    selector: 'person-modal',
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        TooltipModule,
        DropdownModule,
        FormlyModule,
        InputDatePickerComponent,
        SelectModule,
    ],
    templateUrl: './person-modal.component.html',
    styleUrls: ['./person-modal.component.scss']
})
export class PersonModalComponent implements OnInit, OnDestroy {

  tenantId = this._tenantService.getTenantId();
  tenantInfos: TenantInfo;
  @Input() user?: UserModel;
  @Input() me?: Me;

  @Input() isUser = false;

  modalStatus = {
    isCreate: true,
    isPatch: false,
  }

  personForm: FormGroup;
  requestUserModel: UserRequest = {};

  requestMe: MeRequest = {};

  staticFields: UserField[] = [];
  fields: FormlyFieldConfig[];
  typeExtraFields: TypeExtraField[];

  selectedFile: File | null = null;
  srcImage!: Observable<SafeUrl>;
  bucketNameImage = 'user-profile-images';
  file = false;

  private _destroy$: Subject<void> = new Subject<void>();

  constructor(
    public bsModalRef: BsModalRef,
    private _tenantService: TenantService,
    private _usersService: UsersService,
    private _meService: MeService,
    private _newMeService: MeService,
    private _infoService: InfoService,
    private _extraFieldsUtilsService: ExtraFieldsUtilsService,
    private _storageUtilsService: StorageUtilsService,
    private _toastrService: ToastrService,
    private _communicationService: CommunicationService,
    private _translateService: TranslateService,
  ) {
    this.personForm = new FormGroup({
      displayName: new FormControl(null),
      userName: new FormControl(null, [Validators.required]),
      email: new FormControl(null, [Validators.email]),
      pinCode: new FormControl(null, [Validators.pattern(/^\d+$/)]),
      headquarterId: new FormControl(null),
      timezoneName: new FormControl(null),
      cultureInfoName: new FormControl(null),
      licenseType: new FormControl(null),
      licenseNumber: new FormControl(null),
      licenseExpirationDate: new FormControl(null),
    });
  }

  ngOnInit(): void {
    this.initUser();
  }

  private initUser() {
    this.getTenantInfos().pipe(takeUntil(this._destroy$)).subscribe();
    this.getExtraField().pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        // Applica i validator subito dopo aver ottenuto gli staticFields
        this.setInputDisable();
        
        if (this.isUser && !this.user) {
          this.user = {};
          this.user.extraFields = {};
          this.fields = this.typeExtraFields.map(field =>
            this._extraFieldsUtilsService.createFieldConfig(field, this.user.extraFields));
        }
    
        if (this.isUser && this.user?.id) {
          this.modalStatus.isPatch = true;
          this.modalStatus.isCreate = false;
          this.setUser().pipe(takeUntil(this._destroy$)).subscribe();
        }
    
        if (this.me && !this.user) {
          this.setMe().pipe(takeUntil(this._destroy$)).subscribe();
        }
      }
    });
  }

  getTenantInfos(): Observable<TenantInfo> {
    return this._infoService.getTenantInfos()
      .pipe(
        takeUntil(this._destroy$),
        tap(response => {
          if (response) {
            this.tenantInfos = response;
            this.personForm.get('pinCode').setValidators([
              Validators.minLength(this.tenantInfos.minUserPinCodeLength),
              Validators.maxLength(this.tenantInfos.maxUserPinCodeLength)
            ]);
          }
        })
      );
  }

  getExtraField(): Observable<void> {
    return this._usersService.listUserFields$()
      .pipe(
        takeUntil(this._destroy$),
        map(response => {
          this.staticFields = response.items.filter(x => x.isStatic === true);
          const extraFields = response.items.filter(x => x.isStatic === false);
          
          this.typeExtraFields = extraFields.map(x => x as TypeExtraField);
        })
      );
  }

  isStaticFieldRequired(fieldName: string): boolean {
    const staticFieldName: string = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    return this.staticFields.some(field => field.name === staticFieldName && field.required === true);
  }

  private setInputDisable() {
    this.staticFields.forEach(staticField => {
      let fieldName = staticField.name.charAt(0).toLowerCase() + staticField.name.slice(1);      
      const control = this.personForm.get(fieldName);
      
      if (control) {
        if (staticField.readonly) {
          control.disable();
        }
        
        if (staticField.required) {
          const currentValidators = control.validator;
          let validators = [];
          
          if (currentValidators) {
            validators.push(currentValidators);
          }
          
          validators.push(Validators.required);
          control.setValidators(validators);
          control.updateValueAndValidity();
        }
      }
    });
  }

  private patchPersonForm(response: Me | UserModel): void {
    const licenseExpirationDate = response.licenseExpirationDate ? new Date(response.licenseExpirationDate) : null;
    
    this.personForm.patchValue({
      displayName: response.displayName,
      userName: response.userName,
      email: response.email,
      headquarterId: response.headquarterId,
      timezoneName: response.timeZoneName,
      cultureInfoName: response.cultureInfoName,
      pinCode: response.pinCode,
      licenseType: response.licenseType,
      licenseNumber: response.licenseNumber,
      licenseExpirationDate: licenseExpirationDate,
    });

    this.personForm.get('userName').disable();
    this.setInputDisable();
  }

  private setupExtraFields(entity: Me | UserModel): void {
    if (entity) {
      entity.extraFields = entity.extraFields ? entity.extraFields : {};
      entity.extraFields = this._extraFieldsUtilsService.parseDate(this.typeExtraFields, entity.extraFields, false);
      this.fields = this.typeExtraFields.map(field =>
        this._extraFieldsUtilsService.createFieldConfig(field, entity.extraFields));
    }
  }

  setUser(): Observable<UserModel> {
    return this._usersService.getUser$(this.tenantId, this.user.id)
      .pipe(
        takeUntil(this._destroy$),
        tap(response => {
          this.user = response;
          this.patchPersonForm(response);
          this.setupExtraFields(this.user);
        }),
        finalize(() => {
          this.srcImage = this._usersService.getUserImage$(this.tenantId, this.user.id, 140, 140);
        })
      );
  }

  setMe(): Observable<Me> {
    const observable = this._meService.getMe$().pipe(
      tap(response => {
        this.me = response;
        this.patchPersonForm(response);
        this.setupExtraFields(this.me);
      })
    );

    this.srcImage = this._newMeService.getMeImage$(this.tenantId, 140, 140, true);
    return observable;
  }

  onHeadquarterSelected(headquarter: Headquarter) {
    this.personForm.patchValue({
      headquarterId: headquarter?.id ?? null
    });
  }

  onSelectedTimezone(timezone: TimezoneOption) {
    this.personForm.patchValue({
      timezoneName: timezone?.name || null
    });
  }

  onSelectedCultureInfos(cultureInfo: CultureInfo) {
    this.personForm.patchValue({
      cultureInfoName: cultureInfo?.name || null
    });
  }

  onSelectedLicenseType(licenseType: LicenseType) {
    this.personForm.patchValue({
      licenseType: licenseType?.value || null
    });
  }

  onFileSelected($event: Event): void {
    this.selectedFile = this._storageUtilsService.getFileSelect($event);
    this.srcImage = this._storageUtilsService.onFileSelected(this.selectedFile);
  }

  private validateRequiredStaticFields(): boolean {
    let isValid = true;

    this.staticFields.forEach(staticField => {
      if (staticField['required']) {
        const fieldName = staticField.name.charAt(0).toLowerCase() + staticField.name.slice(1);
        const control = this.personForm.get(fieldName);
        
        if (control) {
          const value = control.value;
          
          // Controlla se il valore è null, undefined, stringa vuota o data non valida
          const isEmpty = value === null || 
                         value === undefined || 
                         (typeof value === 'string' && value.trim() === '') ||
                         (value instanceof Date && isNaN(value.getTime()));
          
          if (isEmpty) {
            control.setErrors({ required: true });
            control.markAsTouched();
            isValid = false;
          } else if (control.hasError('required')) {
            // Rimuovi l'errore required se il campo ora ha un valore valido
            const errors = { ...control.errors };
            delete errors['required'];
            control.setErrors(Object.keys(errors).length > 0 ? errors : null);
          }
        }
      }
    });

    return isValid;
  }

  onSubmit(): void {
    this.personForm.markAllAsTouched();
    
    // Valida i campi required statici manualmente
    const staticFieldsValid = this.validateRequiredStaticFields();
    
    if (this.personForm.invalid || !staticFieldsValid) {
      this._toastrService.error(this._translateService.instant('REGISTRATION.MESSAGES.ERROR_REQUIRED_FIELDS'));
      return;
    }

    this._storageUtilsService.uploadFile$(this.selectedFile, this.bucketNameImage)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          if (this.requestUserModel && this.user) {
            this.requestUserModel.imageFileId = response?.body?.id !== undefined ? response.body.id : (this.user?.imageFileId ?? null);
          }

          if (this.requestMe && this.me) {
            this.requestMe.imageFileId = response?.body?.id !== undefined ? response.body.id : (this.me?.imageFileId ?? null);
          }

          if (this.me) {
            this.patchMe();
            return;
          }

          if (this.modalStatus.isCreate) {
            this.addUser();
          }

          if (this.modalStatus.isPatch) {
            this.patchUser();
          }
        }
      });
  }

  addUser(): void {
    this.createUserRequest();

    this._usersService.createUser$(this.tenantId, this.requestUserModel)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          if (response.id) {
            this._communicationService.sendEvent();
            this.bsModalRef.hide();
          }
        }
      });
  }

  patchUser(): void {
    this.createUserRequest();
    this._usersService.updateUser$(this.tenantId, this.user.id, this.requestUserModel)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          if (response.id) {
            this._communicationService.sendEvent();
            this.bsModalRef.hide();
          }
        }
      });
  }

  patchMe(): void {
    this.createPatchMeRequest();
    this._meService.patchMe$(this.requestMe)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          if (response.id) {
            this._communicationService.sendEventToUpdateNavbar();
            this.bsModalRef.hide();
          }
        }
      });
  }

  private populateRequestFromForm<T extends UserRequest | MeRequest>(request: T): T {
    // Gestione extra fields
    if (this.typeExtraFields.length > 0) {
      request = this._extraFieldsUtilsService.updateRequestFromForm(this.typeExtraFields, this.personForm.value, request);
      request.extraFields = this._extraFieldsUtilsService.parseBool(this.typeExtraFields, request.extraFields);
    }

    if (!request.extraFields) {
      request.extraFields = {};
    }

    // Campi comuni
    request.email = this.personForm.get('email')?.value;
    request.displayName = this.personForm.get('displayName')?.value;
    request.pinCode = this.personForm.get('pinCode')?.value;
    request.cultureInfoName = this.personForm.get('cultureInfoName')?.value;
    request.timeZoneName = this.personForm.get('timezoneName')?.value;
    request.licenseType = this.personForm.get('licenseType')?.value;
    request.licenseNumber = this.personForm.get('licenseNumber')?.value;
    request.licenseExpirationDate = this.personForm.get('licenseExpirationDate')?.value?.toISOString();

    return request;
  }

  private populateUserRequestSpecificFields(request: UserRequest): UserRequest {
    request.userName = this.personForm.get('userName')?.value;
    request.headquarterId = this.personForm.get('headquarterId')?.value;
    return request;
  }

  private createUserRequest(): void {
    this.requestUserModel = this.populateRequestFromForm(this.requestUserModel);
    this.requestUserModel = this.populateUserRequestSpecificFields(this.requestUserModel);
  }

  private createPatchMeRequest(): void {
    this.requestMe = this.populateRequestFromForm(this.requestMe);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

}
